<script setup lang="ts">
import DataTableFilterPanel from '@/shared/components/datatable/DataTableFilterPanel.vue'
import LogFilters from '@/features/sales/components/LogFilters.vue'
import { useAdminListSalesDailyLogs } from '@swipe4work/api-orval'
import type { AdminListSalesDailyLogsParams, ListSalesDailyLogsSalesDailyLogDto } from '@swipe4work/models-orval'
import { toDateOnlyString } from '@/features/sales/composables/useSalesMetricsAdapter'

definePage({
  path: '/sales/sales-metrics/all-logs',
  name: '/sales/SalesMetrics/all-logs',
  meta: {
    permission: {
      resource: 'SalesMetrics',
      level: 'Read',
    },
  },
})

type LogFilterState = {
  salesPersonId: number | null
  logDate: {
    from: Date | null
    to: Date | null
  } | null
}

const {
  filters,
  draft,
  searchInput,
  search,
  sortField,
  sortDescending,
  offset,
  rows,
  ROWS_PER_PAGE_OPTIONS,
  activeFilterCount,
  onFilterPanelShow,
  onFilterApply,
  onFilterClear,
  onSort,
  onPage,
} = useDataTableFilters<LogFilterState>({
  schema: {
    salesPersonId: 'id',
    logDate: 'dateRange',
  },
  defaultFilters: () => ({
    salesPersonId: null,
    logDate: null,
  }),
  persistToStorage: 'sales-all-logs',
  defaultPageSize: 10,
  defaultSortField: 'date',
  defaultSortDescending: true,
})

const params = computed<AdminListSalesDailyLogsParams>(() => ({
  search: search.value,
  salesPersonId: filters.salesPersonId ?? undefined,
  dateFrom: filters.logDate?.from ? (toDateOnlyString(filters.logDate.from) as unknown as Date) : undefined,
  dateTo: filters.logDate?.to ? (toDateOnlyString(filters.logDate.to) as unknown as Date) : undefined,
  sortField: sortField.value,
  sortDescending: sortDescending.value,
  limit: rows.value,
  offset: offset.value,
}))

const { data, isPending, isError } = useAdminListSalesDailyLogs(params)

const logs = computed<ListSalesDailyLogsSalesDailyLogDto[]>(() => data.value?.logs ?? [])
const totalCount = computed(() => data.value?.totalCount ?? 0)

const formatDate = (date: Date | string) =>
  new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(new Date(date))
</script>

<template>
  <Card>
    <template #content>
      <div class="flex flex-col gap-4">
        <div class="flex items-center justify-between gap-4">
          <h3 class="text-lg font-semibold">
            Alle sales logs
          </h3>
          <div class="flex items-center gap-3">
            <DataTableFilterPanel
              :active-filter-count="activeFilterCount"
              @show="onFilterPanelShow"
              @apply="onFilterApply"
              @clear="onFilterClear"
            >
              <LogFilters :draft="draft" :show-sales-person-filter="true" />
            </DataTableFilterPanel>

            <IconField class="max-w-xs flex-1">
              <InputIcon class="fa-regular fa-search" />
              <InputText v-model="searchInput" placeholder="Zoeken..." class="w-full" />
              <InputIcon
                v-if="searchInput"
                class="fa-regular fa-xmark cursor-pointer"
                style="pointer-events: auto"
                @click="searchInput = ''"
              />
            </IconField>
          </div>
        </div>

        <div v-if="isPending" class="flex flex-col gap-2">
          <Skeleton v-for="n in 5" :key="n" height="3rem" />
        </div>
        <Message v-else-if="isError" severity="error">
          Er is iets misgegaan bij het laden van logs.
        </Message>
        <div v-else class="overflow-x-auto">
          <DataTable
            :value="logs"
            :lazy="true"
            :first="offset"
            :rows="rows"
            :rows-per-page-options="ROWS_PER_PAGE_OPTIONS"
            :total-records="totalCount"
            :sort-field="sortField"
            :sort-order="sortDescending ? -1 : 1"
            paginator
            paginator-template="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
            current-page-report-template="{first}-{last} van {totalRecords}"
            size="small"
            class="w-full"
            row-hover
            @page="onPage"
            @sort="onSort"
          >
            <template #empty>
              <span class="text-sm text-surface-400">Geen logs gevonden.</span>
            </template>
            <Column field="salesPersonName" header="Sales persoon" sortable style="width: 10rem">
              <template #body="{ data: log }">
                <div class="flex flex-col">
                  <span class="text-sm font-medium">{{ log.salesPersonName }}</span>
                  <span class="text-xs text-surface-400">{{ log.salesPersonEmail }}</span>
                </div>
              </template>
            </Column>
            <Column field="date" header="Datum" sortable style="width: 8rem">
              <template #body="{ data: log }">
                {{ formatDate(log.date) }}
              </template>
            </Column>
            <Column field="calledCompanies" header="Gebeld" sortable style="width: 6rem" />
            <Column field="conversations" header="Gesprekken" sortable style="width: 7rem" />
            <Column field="demoCount" header="Demo's" sortable style="width: 6rem" />
            <Column field="outboundDemoCount" header="Outbound" style="width: 7rem" />
            <Column header="Conv. Bel→Gesprek" sortable field="callToConversationRate" style="width: 10rem">
              <template #body="{ data: log }">
                <span class="text-sm font-mono">{{ log.callToConversationRate.toFixed(1) }}%</span>
              </template>
            </Column>
            <Column header="Conv. Gesprek→Outbound demo" sortable field="conversationToDemoRate" style="width: 12rem">
              <template #body="{ data: log }">
                <span class="text-sm font-mono">{{ log.conversationToDemoRate.toFixed(1) }}%</span>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </template>
  </Card>
</template>
